import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

// Remix Icons
import { 
    RiSearch2Line, 
    RiMagicLine, 
    RiCheckDoubleLine, 
    RiCloseCircleLine, 
    RiTimeLine, 
    RiAwardLine, 
    RiHistoryLine, 
    RiBuilding4Line, 
    RiErrorWarningLine, 
    RiRefreshLine, 
    RiFileTextLine 
} from 'react-icons/ri';

export default function CompanyHRDashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchMobile, setSearchMobile] = useState('');
    const [searchResult, setSearchResult] = useState(null);
    const [searchError, setSearchError] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);

    // Status update modal state
    const [selectedApplicant, setSelectedApplicant] = useState(null);
    const [updateStatusVal, setUpdateStatusVal] = useState('selected');
    const [updateRemarks, setUpdateRemarks] = useState('');
    const [updateSuccess, setUpdateSuccess] = useState('');

    const fetchHRStats = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/dashboard/hr');
            if (res.data.success) setData(res.data);
        } catch (err) {
            console.error('Failed to load HR stats');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHRStats();
    }, []);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchMobile.trim()) return;
        setSearchLoading(true);
        setSearchError('');
        setSearchResult(null);

        try {
            const res = await axios.get(`/api/applicants/lookup/${encodeURIComponent(searchMobile.trim())}`);
            if (res.data.success) {
                setSearchResult(res.data.applicant);
            }
        } catch (err) {
            setSearchError(err.response?.data?.message || 'No candidate found with this mobile number or email.');
        } finally {
            setSearchLoading(false);
        }
    };

    const handleStatusSubmit = async (e) => {
        e.preventDefault();
        if (!selectedApplicant || !user?.company_id) return;

        try {
            const res = await axios.post('/api/applicant-companies/status', {
                applicant_id: selectedApplicant.id,
                company_id: user.company_id,
                status: updateStatusVal,
                remarks: updateRemarks,
            });

            if (res.data.success) {
                setUpdateSuccess(`Status marked as '${updateStatusVal.toUpperCase()}' successfully!`);
                setSelectedApplicant(null);
                fetchHRStats();
                if (searchResult && searchResult.id === selectedApplicant.id) {
                    handleSearch({ preventDefault: () => {} });
                }
            }
        } catch (err) {
            alert('Failed to update status.');
        }
    };

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-16 text-center text-slate-400">
                <RiRefreshLine className="w-8 h-8 animate-spin mx-auto text-amber-500 mb-2" />
                <p className="text-xs font-bold">Loading Stall Interview Portal...</p>
            </div>
        );
    }

    const company = data?.company;
    const stats = data?.stats || {};
    const candidates = data?.candidates || [];

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
            
            {/* Stall Header */}
            <div className="glass-card rounded-3xl p-6 border bg-gradient-to-r from-amber-500/10 via-slate-900/40 to-indigo-500/10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <span className="text-[11px] font-extrabold uppercase tracking-wider text-amber-400 bg-amber-500/10 px-3 py-1 rounded-full border border-amber-500/20">
                            Stall Interview Portal
                        </span>
                        <h1 className="text-3xl font-black mt-2">
                            {company?.company_name || 'Company HR Stall'}
                        </h1>
                        <p className="text-xs font-semibold text-slate-400 mt-1">
                            📍 Location: {company?.office_location || 'Stall Area'} • Openings: <span className="text-indigo-400 font-bold">{company?.openings || 'Active'}</span>
                        </p>
                    </div>

                    {/* Stats pill */}
                    <div className="flex items-center space-x-3 text-center">
                        <div className="p-3.5 glass-card rounded-2xl border">
                            <div className="text-xl font-black">{stats.total_assigned || 0}</div>
                            <div className="text-[10px] text-slate-400 font-bold uppercase">Assigned</div>
                        </div>

                        <div className="p-3.5 glass-card rounded-2xl border border-emerald-500/30">
                            <div className="text-xl font-black text-emerald-400">{stats.selected || 0}</div>
                            <div className="text-[10px] text-emerald-400 font-bold uppercase">Selected</div>
                        </div>

                        <div className="p-3.5 glass-card rounded-2xl border border-rose-500/30">
                            <div className="text-xl font-black text-rose-400">{stats.rejected || 0}</div>
                            <div className="text-[10px] text-rose-400 font-bold uppercase">Rejected</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Notifications */}
            {updateSuccess && (
                <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-semibold flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <RiCheckDoubleLine className="w-5 h-5 text-emerald-400" />
                        <span>{updateSuccess}</span>
                    </div>
                    <button onClick={() => setUpdateSuccess('')} className="text-xs text-slate-400 hover:text-slate-200 font-bold">Dismiss</button>
                </div>
            )}

            {/* Mobile Lookup Section */}
            <div className="glass-card rounded-3xl p-6 border shadow-xl">
                <div className="max-w-2xl mx-auto text-center space-y-3 mb-6">
                    <span className="text-xs font-black uppercase tracking-widest text-indigo-400 flex items-center justify-center space-x-1">
                        <RiMagicLine className="w-4 h-4 text-amber-400" />
                        <span>Candidate Arrival Lookup</span>
                    </span>
                    <h2 className="text-2xl font-black">Enter Candidate Mobile Number</h2>
                    <p className="text-xs font-semibold text-slate-400">Instantly pull candidate details, resume, and previous job fair stall visit history!</p>

                    <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2.5 mt-4">
                        <div className="relative flex-1">
                            <RiSearch2Line className="w-5 h-5 text-slate-400 absolute left-3.5 top-3.5" />
                            <input
                                type="text"
                                value={searchMobile}
                                onChange={e => setSearchMobile(e.target.value)}
                                placeholder="Enter mobile number e.g. 9988776655"
                                className="w-full pl-11 pr-4 py-3 rounded-2xl glass-input text-base font-bold tracking-wide"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={searchLoading}
                            className="px-6 py-3 rounded-2xl bg-gradient-to-r from-amber-500 via-indigo-600 to-purple-600 hover:brightness-110 text-white font-black text-sm shadow-lg shadow-amber-500/20 transition-all flex items-center justify-center space-x-2"
                        >
                            {searchLoading ? 'Searching...' : 'Lookup Candidate'}
                        </button>
                    </form>
                </div>

                {searchError && (
                    <div className="max-w-2xl mx-auto p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-semibold text-center flex items-center justify-center space-x-2">
                        <RiErrorWarningLine className="w-5 h-5 text-red-500" />
                        <span>{searchError}</span>
                    </div>
                )}

                {/* Instant Candidate Search Result Card */}
                {searchResult && (
                    <div className="max-w-3xl mx-auto glass-card rounded-3xl p-6 border space-y-6 shadow-2xl animate-fade-in">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800/80 pb-4">
                            <div>
                                <div className="flex items-center space-x-2">
                                    <span className="text-2xl font-black">{searchResult.name}</span>
                                    <span className="text-xs bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 px-2.5 py-0.5 rounded-full font-bold">
                                        ID #{searchResult.id}
                                    </span>
                                </div>
                                <div className="text-xs text-slate-400 font-semibold mt-1">
                                    📱 Phone: <span className="font-bold">{searchResult.phone_number}</span> | ✉️ {searchResult.email}
                                </div>
                            </div>

                            <button
                                onClick={() => setSelectedApplicant(searchResult)}
                                className="px-5 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 text-white font-black text-xs shadow-lg shadow-emerald-600/30 flex items-center justify-center space-x-1.5"
                            >
                                <RiAwardLine className="w-4 h-4" />
                                <span>Mark Candidate Status</span>
                            </button>
                        </div>

                        {/* Details */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                            <div className="p-3.5 glass-card rounded-2xl border">
                                <div className="text-slate-400 text-[10px] uppercase font-bold">Education</div>
                                <div className="font-extrabold mt-0.5">{searchResult.highest_education || 'N/A'}</div>
                            </div>
                            <div className="p-3.5 glass-card rounded-2xl border">
                                <div className="text-slate-400 text-[10px] uppercase font-bold">Experience</div>
                                <div className="font-extrabold text-amber-400 mt-0.5">{searchResult.experience_type || 'Fresher'}</div>
                            </div>
                            <div className="p-3.5 glass-card rounded-2xl border">
                                <div className="text-slate-400 text-[10px] uppercase font-bold">Location</div>
                                <div className="font-extrabold mt-0.5">{searchResult.city || 'N/A'}</div>
                            </div>
                            <div className="p-3.5 glass-card rounded-2xl border">
                                <div className="text-slate-400 text-[10px] uppercase font-bold">Resume</div>
                                {searchResult.resume_path ? (
                                    <a href={`/storage/${searchResult.resume_path}`} target="_blank" rel="noreferrer" className="text-indigo-400 underline font-bold mt-0.5 block">
                                        View Resume 📄
                                    </a>
                                ) : (
                                    <span className="text-slate-400 font-semibold mt-0.5 block">Not Uploaded</span>
                                )}
                            </div>
                        </div>

                        {/* Preferred Sectors */}
                        <div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase mb-1">Preferred Sectors</div>
                            <div className="flex flex-wrap gap-1.5">
                                {searchResult.job_applying_for ? searchResult.job_applying_for.split(',').map((sec, idx) => (
                                    <span key={idx} className="text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30 px-2.5 py-1 rounded-xl font-bold">
                                        {sec.trim()}
                                    </span>
                                )) : <span className="text-xs text-slate-400 font-semibold">General</span>}
                            </div>
                        </div>

                        {/* Previous Stall Visit History across Job Fair */}
                        <div className="pt-4 border-t border-slate-800/80">
                            <div className="text-xs font-black text-amber-400 uppercase tracking-wider mb-3 flex items-center space-x-1.5">
                                <RiHistoryLine className="w-4 h-4" />
                                <span>Candidate's Visit History Across Job Fair</span>
                            </div>

                            <div className="space-y-2">
                                {searchResult.company_visits?.map((v, i) => (
                                    <div key={v.id} className="p-3.5 glass-card rounded-2xl border flex items-center justify-between text-xs">
                                        <div>
                                            <span className="font-extrabold">{v.company?.company_name}</span>
                                            <span className="text-[10px] text-slate-400 font-semibold ml-2">Order #{v.visit_order || (i+1)}</span>
                                            {v.remarks && <div className="text-[11px] text-slate-400 italic mt-0.5">"{v.remarks}"</div>}
                                        </div>

                                        <span className={`px-2.5 py-1 rounded-xl text-xs font-black uppercase ${
                                            v.status === 'selected' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                                            v.status === 'rejected' ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' :
                                            'bg-indigo-500/20 text-amber-400 border border-indigo-500/30'
                                        }`}>
                                            {v.status}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Candidates Table Assigned to HR's Stall */}
            <div className="glass-card rounded-3xl p-6 border">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-black">Candidates Assigned to Your Stall</h2>
                    <span className="text-xs text-slate-400 font-bold">{candidates.length} candidates in queue</span>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-800">
                            <tr>
                                <th className="p-4">Queue Order</th>
                                <th className="p-4">Candidate Name</th>
                                <th className="p-4">Mobile Number</th>
                                <th className="p-4">Current Status</th>
                                <th className="p-4">Remarks</th>
                                <th className="p-4 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/60">
                            {candidates.map((c, idx) => (
                                <tr key={c.id} className="hover:bg-slate-800/40 transition-colors">
                                    <td className="p-4 font-black text-indigo-400 text-sm">
                                        # {c.visit_order || (idx + 1)}
                                    </td>
                                    <td className="p-4 font-extrabold">
                                        {c.applicant?.name}
                                    </td>
                                    <td className="p-4 text-xs font-bold text-slate-400">
                                        📱 {c.applicant?.phone_number}
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-2.5 py-1 rounded-xl text-xs font-black uppercase ${
                                            c.status === 'selected' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                                            c.status === 'rejected' ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' :
                                            c.status === 'visited' ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' :
                                            'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                        }`}>
                                            {c.status}
                                        </span>
                                    </td>
                                    <td className="p-4 text-xs text-slate-400 font-medium max-w-xs truncate">
                                        {c.remarks || '—'}
                                    </td>
                                    <td className="p-4 text-right">
                                        <button
                                            onClick={() => setSelectedApplicant(c.applicant)}
                                            className="px-3.5 py-1.5 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30 text-xs font-bold transition-all"
                                        >
                                            Update Status
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Status Update Modal */}
            {selectedApplicant && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
                    <div className="w-full max-w-md glass-card rounded-3xl p-6 border shadow-2xl">
                        <div className="border-b border-slate-800/80 pb-3 mb-4">
                            <span className="text-xs font-extrabold text-amber-400 uppercase tracking-widest">Stall Interview Decision</span>
                            <h2 className="text-xl font-black mt-0.5">{selectedApplicant.name}</h2>
                            <div className="text-xs text-slate-400 font-bold">📱 {selectedApplicant.phone_number}</div>
                        </div>

                        <form onSubmit={handleStatusSubmit} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-300 mb-2">Select Interview Result / Status *</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { val: 'selected', label: 'Selected / Shortlisted', color: 'bg-emerald-600 border-emerald-500' },
                                        { val: 'visited', label: 'Visited / Under Review', color: 'bg-indigo-600 border-indigo-500' },
                                        { val: 'rejected', label: 'Rejected', color: 'bg-rose-600 border-rose-500' },
                                        { val: 'no_show', label: 'No Show', color: 'bg-slate-700 border-slate-600' },
                                    ].map(opt => (
                                        <button
                                            type="button"
                                            key={opt.val}
                                            onClick={() => setUpdateStatusVal(opt.val)}
                                            className={`p-2.5 rounded-xl border text-xs font-black transition-all ${
                                                updateStatusVal === opt.val
                                                    ? `${opt.color} text-white shadow-lg`
                                                    : 'glass-card text-slate-400 border-slate-800'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-300 mb-1">Interview Remarks / Feedback</label>
                                <textarea
                                    rows="3"
                                    value={updateRemarks}
                                    onChange={e => setUpdateRemarks(e.target.value)}
                                    placeholder="e.g. Good communication skills. Shortlisted for Technical Round 2."
                                    className="w-full px-4 py-2.5 rounded-2xl glass-input text-xs font-medium"
                                ></textarea>
                            </div>

                            <div className="flex justify-end space-x-3 pt-3 border-t border-slate-800/80">
                                <button
                                    type="button"
                                    onClick={() => setSelectedApplicant(null)}
                                    className="px-4 py-2 rounded-xl glass-card text-slate-300 text-xs font-bold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-5 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 text-white text-xs font-black shadow-lg shadow-emerald-600/30"
                                >
                                    Confirm Decision
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
